// Черновик точки: что курьер уже набрал, но ещё не отметил выполненным.
// Живёт в localStorage — приложение могут закрыть, телефон может разрядиться.

import type { PayMethod, VisitCloseBody, VisitSwap } from '@/services/courier';

export interface PointDraft {
  /** d — отдал, r — забрал и заменил тем же товаром. */
  lines: Record<string, { d: number; r: number }>;
  /** Обмены: забрал одно, отдал другое. */
  swaps: VisitSwap[];
  paid: string;
  method: PayMethod;
  comment: string;
}

const key = (shiftId: string, pointId: string) => `resstro-courier-draft:v1:${shiftId}:${pointId}`;

export function loadDraft(shiftId: string, pointId: string): PointDraft | null {
  try {
    const raw = localStorage.getItem(key(shiftId, pointId));
    if (!raw) return null;
    const draft = JSON.parse(raw) as PointDraft;
    return { ...draft, swaps: draft.swaps ?? [] };
  } catch {
    return null;
  }
}

export function saveDraft(shiftId: string, pointId: string, draft: PointDraft): void {
  try {
    localStorage.setItem(key(shiftId, pointId), JSON.stringify(draft));
  } catch {
    /* переполнено или приватный режим — без черновика */
  }
}

export function clearDraft(shiftId: string, pointId: string): void {
  try {
    localStorage.removeItem(key(shiftId, pointId));
  } catch {
    /* ignore */
  }
}

/** После смены черновики не нужны — чистим, чтобы не копились. */
export function clearShiftDrafts(shiftId: string): void {
  try {
    const prefix = `resstro-courier-draft:v1:${shiftId}:`;
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const k = localStorage.key(i);
      if (k?.startsWith(prefix)) localStorage.removeItem(k);
    }
  } catch {
    /* ignore */
  }
}

/** Точку не приняли из очереди — возвращаем набранное в черновик, чтобы поправить, а не набирать заново. */
export function draftFromBody(body: VisitCloseBody): PointDraft {
  const lines: PointDraft['lines'] = {};
  for (const l of body.lines) lines[l.itemId] = { d: l.delivered, r: 0 };
  const swaps: VisitSwap[] = [];
  for (const s of body.swaps ?? []) {
    if (s.fromItemId === s.toItemId && s.qty === s.toQty) {
      const cur = lines[s.fromItemId] ?? { d: 0, r: 0 };
      lines[s.fromItemId] = { ...cur, r: Math.round((cur.r + s.qty) * 1000) / 1000 };
    } else swaps.push(s);
  }
  return { lines, swaps, paid: String(Math.round(body.paidAmount / 100)), method: body.paymentMethod, comment: body.comment ?? '' };
}

/** Курьер на точке уже что-то набрал — иначе черновик не нужен и точка не «начата». */
export function draftHasWork(d: PointDraft | null): boolean {
  if (!d) return false;
  return Object.keys(d.lines).length > 0 || d.swaps.length > 0 || d.paid.trim() !== '' || d.comment.trim() !== '';
}
